import React, { useState } from 'react';
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CBadge,
  CButton,
  CModalHeader,
  CModal,
  CModalBody,
  CTooltip,
  CModalTitle,
} from '@coreui/react';
import { formatDateTime } from '../../../helpers';
import { Link } from 'react-router-dom';
import _t from 'counterpart';
import {
  ICallDetail,
  ICommunication,
  IEmailDetail,
  ITableField,
  IMessageDetail,
  ICommentDetail,
  ISmsWhatsAppDetail,
  IMt5PushDetail,
} from '../types';
import { DEPOSIT_LINK_SMS_MASKED_BODY } from '../../../services/BackendService';
import DOMPurify from 'dompurify';
import PaginationTable from '../../../components/PaginationTable';

const CommunicationTypeBadge = ({ type }: { type: string }) => {
  const colors: any = {
    CALL: 'success',
    EMAIL: 'danger',
    MESSAGE: 'primary',
    COMMENT: 'info',
    SMS: 'warning',
    WHATSAPP: 'success',
    MT5_PUSH: 'primary',
  };

  return <CBadge color={colors[type] ?? 'secondary'}>{type}</CBadge>;
};

const CallDetail = (detail: ICallDetail) => {
  const { answered, recordingFileLink } = detail;

  return (
    <div className="d-flex align-items-center justify-content-between">
      <div>
        {/* eslint-disable jsx-a11y/media-has-caption */}
        {answered && (
          <audio controls>
            <source src={recordingFileLink} type="audio/wav" />
          </audio>
        )}
      </div>
      {answered === null && (
        <div className="badge-outline-secondary">
          <span>{_t('customer.communication.unknown')}</span>
        </div>
      )}
      {answered === false && (
        <div className="badge-outline-danger">
          <span>{_t('customer.communication.not-answered')}</span>
        </div>
      )}
      {answered === true && (
        <div className="badge-outline-success">
          <span className="mr-1">{_t('customer.communication.answered')}</span>
        </div>
      )}
    </div>
  );
};

const MessageDetail = (detail: IMessageDetail) => {
  if (detail) {
    const { readAt, subject, viaPush } = detail;

    return (
      <div className="d-flex align-items-center justify-content-between">
        <span className="ml-1">{subject}</span>
        {/* One row with the subject — rendered below it, the badges sat left-aligned on their own line. */}
        <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
          {viaPush && <CBadge color="success">{_t('customer.communication.via-push')}</CBadge>}
          {!readAt && (
            <div className="badge-outline-danger">
              <span>{_t('customer.communication.not-read')}</span>
            </div>
          )}
          {readAt && (
            <CTooltip content={formatDateTime(readAt)}>
              <div className="badge-outline-success">
                <span>{_t('customer.communication.read')}</span>
              </div>
            </CTooltip>
          )}
        </div>
      </div>
    );
  }
};

const Mt5PushDetail = (detail: IMt5PushDetail) => {
  if (!detail) return null;
  const preview = detail.text.length > 60 ? `${detail.text.slice(0, 60)}…` : detail.text;
  return (
    <span className="ml-1 text-truncate" style={{ maxWidth: '220px' }} title={detail.text}>
      {preview}
    </span>
  );
};

const SmsWhatsAppDetail = (detail: ISmsWhatsAppDetail) => {
  if (!detail) return null;
  const { body, direction, deliveryStatus, linkClickedAt } = detail;
  const isAutologinLink = body === DEPOSIT_LINK_SMS_MASKED_BODY;
  const displayText = isAutologinLink ? _t('customer.autologin-link-sent-masked') : body;
  const preview = displayText.length > 60 ? `${displayText.slice(0, 60)}…` : displayText;
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
      <span className="ml-1 text-truncate" style={{ maxWidth: '220px' }} title={displayText}>
        {preview}
      </span>
      <div className="d-flex align-items-center gap-1">
        {isAutologinLink && linkClickedAt && (
          <CTooltip content={formatDateTime(linkClickedAt)}>
            <CBadge color="success" className="ml-1">
              {_t('customer.autologin-link-clicked')}
            </CBadge>
          </CTooltip>
        )}
        {isAutologinLink && !linkClickedAt && (
          <CBadge color="warning" className="ml-1">
            {_t('customer.autologin-link-not-clicked')}
          </CBadge>
        )}
        {deliveryStatus && (
          <CBadge
            color={
              deliveryStatus === 'delivered'
                ? 'success'
                : deliveryStatus === 'failed' || deliveryStatus === 'undelivered'
                  ? 'danger'
                  : 'secondary'
            }
            className="ml-1"
          >
            {deliveryStatus}
          </CBadge>
        )}
        <CBadge color={direction === 'INBOUND' ? 'info' : 'secondary'}>
          {direction}
        </CBadge>
      </div>
    </div>
  );
};

interface IProps {
  fields: Array<ITableField>;
  data: Array<ICommunication> | null;
  pages?: number;
  page?: number;
  onPageChanged?: (page: number) => void;
  isLoading?: boolean;
}

const CommunicationsTable = ({ data, fields, pages, page, onPageChanged, isLoading }: IProps) => {
  const [emailDetails, setEmailDetails] = useState<IEmailDetail | null>(null);
  const [messageDetails, setMessageDetails] = useState<IMessageDetail | null>(null);
  const [commentDetails, setCommentDetails] = useState<ICommentDetail | null>(null);
  const [smsWhatsAppDetails, setSmsWhatsAppDetails] = useState<ISmsWhatsAppDetail | null>(null);

  const showEmailModal = (detail: IEmailDetail) => {
    setEmailDetails(detail);
  };

  const hideEmailModal = () => {
    setEmailDetails(null);
  };

  const showCommentModal = (detail: ICommentDetail) => {
    setCommentDetails(detail);
  };

  const hideCommentModal = () => {
    setCommentDetails(null);
  };

  const showMessageModal = (detail: IMessageDetail) => {
    setMessageDetails(detail);
  };

  const showSmsWhatsAppModal = (detail: ISmsWhatsAppDetail) => {
    setSmsWhatsAppDetails(detail);
  };

  const hideMessageModal = () => {
    setMessageDetails(null);
  };

  const hideSmsWhatsAppModal = () => {
    setSmsWhatsAppDetails(null);
  };

  const scopedSlots = React.useMemo(
    () => ({
      date: (communication: ICommunication) => <td>{formatDateTime(communication.date)}</td>,
      from: (communication: ICommunication) => (
        <td>
          <Link to={`/customers/${communication.fromCustomerId}`}>
            <div>{communication.fromName}</div>
          </Link>
        </td>
      ),
      to: (communication: ICommunication) => (
        <td>
          {communication.userType === 'customer' && (
            <Link to={`/customers/${communication.toCustomerId}`}>
              <div>{communication.toName}</div>
            </Link>
          )}
          {communication.userType === 'lead' && (
            <Link to={`/leads/${communication.id}`}>
              <div>{communication.toName}</div>
            </Link>
          )}
        </td>
      ),
      type: (communication: ICommunication) => (
        <td>
          <div className="small text-medium-emphasis">
            <CommunicationTypeBadge type={communication.type} />
          </div>
        </td>
      ),
      detail: (communication: ICommunication) => (
        <td>
          {communication.type === 'CALL' && CallDetail(communication.detail as ICallDetail)}
          {communication.type === 'MESSAGE' && MessageDetail(communication.detail as IMessageDetail)}
          {(communication.type === 'SMS' || communication.type === 'WHATSAPP') &&
            SmsWhatsAppDetail(communication.detail as ISmsWhatsAppDetail)}
          {communication.type === 'MT5_PUSH' && Mt5PushDetail(communication.detail as IMt5PushDetail)}
        </td>
      ),
      actions: (communication: ICommunication) => (
        <td>
          {communication.type === 'MESSAGE' && (
            <CButton
              className="communications-table-actions-button"
              onClick={() => showMessageModal(communication.detail as IMessageDetail)}
              color="primary"
            >
              {_t('customer.communication.show-message')}
            </CButton>
          )}
          {communication.type === 'EMAIL' && (
            <CButton
              className="communications-table-actions-button"
              onClick={() => showEmailModal(communication.detail as IEmailDetail)}
              color="primary"
            >
              {_t('customer.communication.show-email')}
            </CButton>
          )}

          {communication.type === 'COMMENT' && (
            <CButton
              className="communications-table-actions-button"
              onClick={() => showCommentModal(communication.detail as ICommentDetail)}
              color="primary"
            >
              {_t('customer.communication.show-comment')}
            </CButton>
          )}

          {(communication.type === 'SMS' || communication.type === 'WHATSAPP') && (
            <CButton
              className="communications-table-actions-button"
              onClick={() => showSmsWhatsAppModal(communication.detail as ISmsWhatsAppDetail)}
              color="primary"
            >
              {_t('customer.communication.show-message')}
            </CButton>
          )}
        </td>
      ),
    }),
    []
  );

  return (
    <CRow>
      <CCol md={12}>
        <CCard className="card card--listing">
          <CCardBody className="card__holder">
            <CCardHeader className="card__header">
              <div className="card__info">
                <h4 className="card__title">{_t('customer.communication.title')}</h4>
              </div>
            </CCardHeader>
            <PaginationTable
              data={data}
              tableFields={fields}
              scopedSlots={scopedSlots}
              pages={pages || 0}
              pagination
              activePage={page}
              onPageChanged={onPageChanged}
              loading={Boolean(isLoading)}
            />
          </CCardBody>
        </CCard>
      </CCol>
      {emailDetails && (
        <CModal show={Boolean(emailDetails)} onClose={hideEmailModal} size="xl">
          <CModalHeader closeButton>
            <CModalTitle>{emailDetails.subject}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailDetails.body) }} />
          </CModalBody>
        </CModal>
      )}

      {commentDetails && (
        <CModal show={Boolean(commentDetails)} onClose={hideCommentModal} size="xl">
          <CModalHeader closeButton>
            <CModalTitle>{_t('customer.communication.comment-title')}</CModalTitle>
          </CModalHeader>
          <CModalBody className="message-details">{commentDetails}</CModalBody>
        </CModal>
      )}

      <CModal show={Boolean(messageDetails)} onClose={hideMessageModal} size="lg">
        <CModalHeader>
          <CModalTitle>{messageDetails?.subject}</CModalTitle>
        </CModalHeader>
        <CModalBody className="message-details">{messageDetails?.text}</CModalBody>
      </CModal>

      <CModal show={Boolean(smsWhatsAppDetails)} onClose={hideSmsWhatsAppModal} size="lg">
        <CModalHeader closeButton>
          <CModalTitle>
            {smsWhatsAppDetails?.channel} ({smsWhatsAppDetails?.direction})
            {smsWhatsAppDetails?.deliveryStatus && (
              <CBadge
                color={
                  smsWhatsAppDetails.deliveryStatus === 'delivered'
                    ? 'success'
                    : smsWhatsAppDetails.deliveryStatus === 'failed' ||
                        smsWhatsAppDetails.deliveryStatus === 'undelivered'
                      ? 'danger'
                      : 'secondary'
                }
                className="ml-2"
              >
                {smsWhatsAppDetails.deliveryStatus}
              </CBadge>
            )}
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="message-details">
          {smsWhatsAppDetails?.body === DEPOSIT_LINK_SMS_MASKED_BODY
            ? _t('customer.autologin-link-sent-masked')
            : smsWhatsAppDetails?.body}
        </CModalBody>
      </CModal>
    </CRow>
  );
};

export default React.memo(CommunicationsTable);
